import { Box, Collapse, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ChevronUp, GraduationCap } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import AccordionActivity from "./AccordionActivity.component";
import {
  AccordionHeader,
  AccordionWrapper,
  CoverImage,
  HeaderContent,
  HeaderRight,
  MetaTypography,
  TitleTypography
} from "./PathCourseAccordion.styles";
import type { PathCourseAccordionProps } from "./PathCourseAccordion.types";
import { ProgressIndexCircle } from "./ProgressIndexCircle.component";
import { handleBorderColor } from "./utils";
import { useActivityAndCourseSelection } from "../../components/DetailsEsploraRestyle/IndexActivitySelected.context";
import { handleTeachersAndTutors } from "../../components/LearningActiviyModal/utils";
import { LEVEL } from "../../consts";
import { useLevel, useType } from "../../hooks";
import { useLearningObjectQuery } from "../../queries";
import { useNavigate } from "../../services";
import type { ReferencedLearningActivity } from "../../types";
import { formatDuration_h_m, hasPassedExpiration } from "../../utils/datetime";
import { StatusBoxesContainer } from "../CarouselCardRestyle/CarouselCard.style";
import { type ActivityItem } from "../CourseActivityCard";

const isClassroomActivity = (learningObjectTypology?: string) =>
  learningObjectTypology === "DA_VIRTUAL_CLASS" || learningObjectTypology === "DA_PHYSICAL_CLASS";

const isEnrolledStatus = (status?: string) => status === "E" || status === "P" || status === "C";

// I docenti di un'aula virtuale/fisica vanno mostrati solo se l'utente è iscritto a una sua edizione:
const getVisibleTeachers = (
  activity: ReferencedLearningActivity,
  t: (key: string, fallback: string) => string
) => {
  const teachersAndTutors = handleTeachersAndTutors(activity.teachers ?? [], activity.tutors ?? [], t);

  if (!isClassroomActivity(activity.learningObjectTypology)) return teachersAndTutors;

  const isEnrolled = isEnrolledStatus(activity.status)
    || (activity.editions ?? []).some((edition) => isEnrolledStatus(edition.status));

  return isEnrolled ? teachersAndTutors : [];
};

export function PathCourseAccordion({
  course,
  ecmRegistration = true,
  ecm_specialization = false,
  index,
  rootId,
  //pathActivities,
  setOpenModalEcm,
  initialActiveLoId,
  defaultOpen
}: PathCourseAccordionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setActivityIdSelected, courseIdSelected, setCourseIdSelected } = useActivityAndCourseSelection();

  const courseIsOpen = courseIdSelected === course.id;

  const handleToggle = () => {
    if(!rootId) return;

    const isForYou = window.location.href.includes("per-te");
    setActivityIdSelected(undefined);

    // close course, reset url to path
    if (courseIsOpen) {
      setCourseIdSelected(undefined);

      navigate(
        navigate.getDetailLink({ id: rootId, isForYou, learningObjectTypology: LEVEL.PATH }),
        { replace: true, state: { preserveScroll: true } }
      );
      return;
    }

    // open course and set url
    setCourseIdSelected(course.id);
    navigate(
      navigate.getDetailLink({ id: course.id, isForYou, learningObjectTypology: LEVEL.COURSE, rootId }),
      { replace: true, state: { preserveScroll: true } }
    );
  };

  const { data: courseDetail, isLoading, refetch } = useLearningObjectQuery({
    enabled: courseIsOpen,
    id: course.id,
    learningObjectTypology: LEVEL.COURSE,
    refetchOnMount: true,
    rootId: rootId ?? null
  });

  // Quando l'accordion è aperto e si torna sulla scheda (es. dopo aver aperto
  // un LO url libero in un nuovo tab), rifà solo la chiamata di questo corso
  // per aggiornare gli stati di completamento delle attività al suo interno.
  useEffect(() => {
    if (!courseIsOpen) return;
    const handleVisibilityChange = () => {
      if (!document.hidden) refetch();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [courseIsOpen, refetch]);

  const levelLabel = useLevel(course.learningObjectTypology);
  const typeLabel = useType(course.learningObjectType, course.learningObjectTypology);
  const isCompleted = course.status === "C";
  const isExpired = hasPassedExpiration(course.expirationDate);
  const isEcmDisabled = !ecmRegistration && ecm_specialization;

  const rawActivities =
    courseDetail && "learningActivities" in courseDetail
      ? courseDetail.learningActivities
      : [];

  const courseActivities: ActivityItem[] = (rawActivities ?? []).map((activity) => ({
    attributes: activity.attributes,
    badge: activity.badge,
    canAccessNow: course.canAccessNow,
    certificate: activity.certificate,
    courseId: course.id,
    disabled: !activity.isEnable || isEcmDisabled,
    duration: activity.duration,
    ecmRegistration: false,
    ecm_specialization: false,
    editions: activity.editions,
    enrollType: activity.enrollType,
    expirationDate: activity.expirationDate,
    externalFileUrl: activity.externalFileUrl,
    grandParentId: activity.grandParentId ?? undefined,
    id: activity.id,
    isEditorial: activity.isEditorial,
    isFavourite: activity.isFavourite,
    isLinkRelative: !!activity.isLinkRelative,
    isMandatory: activity.isMandatory,
    isStandAlone: activity.isStandAlone,
    isSurvey: activity.isSurvey,
    isTest: activity.isTest,
    learningObjectType: activity.learningObjectType,
    learningObjectTypology: activity.learningObjectTypology,
    link: activity.link,
    madeAttempts: activity.madeAttempts ?? undefined,
    maxAttempts: activity.maxAttempts,
    parentId: activity.parentId ?? undefined,
    percentageOfCompletion: activity.percentageOfCompletion,
    rootId: rootId,
    setOpenModalEcm: setOpenModalEcm,
    status: activity.status || "N",
    teachers: getVisibleTeachers(activity, t),
    title: activity.title,
    toolNumber: activity.toolNumber
  }));

  const effectiveTypeLabel = course.isTest
    ? t("test")
    : course.isSurvey
      ? t("survey")
      : typeLabel;

  const completionPercentage = Number.parseFloat(
    course.percentageOfCompletion?.replace("%", "") ?? "0"
  );
  const progressValue = Number.isFinite(completionPercentage) ? completionPercentage : 0;

  const buildMetaTypography = () => {
    const icon = <GraduationCap size={ "1rem" }/>;
    const duration = formatDuration_h_m(course.duration) ? `${formatDuration_h_m(course.duration)}` : "";
    const numberActivities = course.learningActivityNumber > 0
      ? <>
        { icon }{ " " }{ t("activity", { count: course.learningActivityNumber }) }
      </>
      : null;

    const metaParts = [
      levelLabel,
      effectiveTypeLabel,
      duration
    ].filter(Boolean).join(" | ");

    return (
      <Stack
        flexDirection={ "row" }
        alignItems={ "center" }
        gap={ "1rem" }
        flexWrap={ "wrap" }
      >
        <MetaTypography>
          { metaParts }
        </MetaTypography>
        <MetaTypography
          sx={ {
            gap:"0.25rem"
          } }
        >
          { numberActivities }
        </MetaTypography>
      </Stack>
    );
  };
  // Aria label for accessibility (same logic as CarouselCardRestyle)

  let ariaLabel = `${course.title}. ${t("typology_carousel")}:${t("course")}`;

  if (course.duration) {
    ariaLabel = `${ariaLabel}. ${t("duration")}:${formatDuration_h_m(course.duration)}`;
  }

  if (isCompleted) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("completed")}`;
  } else if (isExpired) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("expired")}`;
  } else if (course.isMandatory) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("mandatory")}`;
  }

  if(course.learningActivityNumber === 1 ){
    ariaLabel = `${ariaLabel}. ${t("contains")}:${t("activity_one",{ count:1 })}`;
  }else if(course.learningActivityNumber > 1){
    ariaLabel = `${ariaLabel}. ${t("contains")}:${t("activity_other",{ count:course.learningActivityNumber })}`;
  }

  const accordionPanelId = `path-course-accordion-${course.id}-panel`;

  useEffect(() => {
    defaultOpen && setCourseIdSelected(course.id);
  }, []);

  return (
    <AccordionWrapper
      id={ `path-course-accordion-${course.id}` }
      data-testid="path-course-accordion-root"
      tabIndex={ -1 }
      border={ `0.0625rem solid ${handleBorderColor(isCompleted, !!courseIsOpen, theme)}` }
      $isClosed={ !courseIsOpen }
    >
      <AccordionHeader
        id={ `path-course-accordion-${course.id}-header` }
        data-testid="path-course-accordion-header"
        tabIndex={ 0 }
        aria-label={ ariaLabel }
        aria-controls={ accordionPanelId }
        role="button"
        aria-expanded={ courseIsOpen }
        onClick={ handleToggle }
        onKeyDown={ (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        } }
        sx={ {
          "&:focus-visible": {
            outline: `2px solid ${theme.customColors.headerIcon}`,
            outlineOffset: "-3px",
            borderRadius: theme.spacing(2)
          }
        } }
        position={ "relative" }
        flexWrap={ "wrap" }
      >
        <Stack
          id="header-left"
          flexDirection={ "row" }
          gap={ "1rem" }
          flexGrow={ 1 }
          flexWrap={ "wrap" }
          marginRight={ "1rem" }
        >
          <ProgressIndexCircle
            label={ index + 1 }
            value={ progressValue }
            isCompleted={ isCompleted }
          />


          { course.coverPublicURL && (
            <CoverImage
              alt={ course.title }
              src={ course.coverPublicURL }
              onClick={ (e) => e.stopPropagation() }
            />
          ) }

          <HeaderContent
            id={ `header-title-and-meta-${course.id}` }
            minWidth={ "fit-content" }
          >
            <TitleTypography>{ course.title }</TitleTypography>

            { buildMetaTypography() }

          </HeaderContent>
        </Stack>

        <HeaderRight
          id="header-right"
          sx={ {
            margin: "0rem 1.5rem 0rem auto"
          } }
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={ 0.5 } >
            <StatusBoxesContainer
              attributes={ course.attributes }
              endDate={ undefined }
              expirationDate={ course.expirationDate }
              id={ course.id }
              key={ `${course.id}-${course.status}` }
              isCompleted={ isCompleted }
              isExpired={ isExpired }
              isMandatory={ course.isMandatory }
              isSurvey={ course.isSurvey }
              isTest={ course.isTest }
              learningObjectTypology={ course.learningObjectTypology }
              startDate={ undefined }
            />
          </Box>
        </HeaderRight>

        <Box
          sx={ {
            position: "absolute",
            right: "0.75rem",
            top: "50%",
            transform: "translateY(-50%)" ,
            display: "flex",
            flexShrink: 0
          } }
        >
          { courseIsOpen
            ? <ChevronUp size={ 20 } color={ theme.customColors.textPrimary } />
            : <ChevronDown size={ 20 } color={ theme.customColors.textPrimary } />
          }
        </Box>
      </AccordionHeader>

      <Collapse
        in={ courseIsOpen }
        timeout={ 300 }
        id={ accordionPanelId }
        data-testid="path-course-accordion-panel"
        role="region"
        aria-labelledby={ `path-course-accordion-${course.id}-header` }
      >
        <AccordionActivity
          course={ course }
          isLoading={ isLoading }
          activities={ courseActivities }
          description={ courseDetail?.longDescription }
          initialActiveLoId={ initialActiveLoId }
        />
      </Collapse>
    </AccordionWrapper>
  );
}
